<template>
  <div id="smd-user-page">
    <b-card no-body class="h-100">
      <!-- Header -->
      <b-card-header>
        <b-card-title class="d-flex align-items-center">
          <i class="bi bi-person-badge"></i>
          <span style="margin-left: 5px">User Sosmed</span>
        </b-card-title>
        <div class="d-flex flex-wrap justify-content-end align-items-center gap-5px">
          <!-- Tambah User -->
          <b-button v-if="user_role == 0" variant="outline-info" v-b-modal.modal-add-smd-user>
            <feather-icon icon="PlusIcon" />
            <span>Tambah User Sosmed</span>
          </b-button>
          <!-- Filter Platform -->
          <select v-model="filter_data.platform" class="form-control" style="width:160px" @change="getData">
            <option value="all">Semua Platform</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
          </select>
          <!-- Keyword Search -->
          <b-input-group class="input-group-merge" style="width: 20rem">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input placeholder="Cari username..." v-model="filter_data.key" />
          </b-input-group>
        </div>
      </b-card-header>

      <div>
        <loader v-if="is_loading" />
        <b-table
          v-else
          :items="table_items"
          :fields="table_fields"
          responsive
          striped
          hover
          class="mb-0"
        >
          <!-- No -->
          <template #cell(no)="data">
            {{ (pagination.page - 1) * pagination.items + data.index + 1 }}
          </template>
          <!-- Platform Badge -->
          <template #cell(platform)="data">
            <b-badge :variant="data.item.platform === 'facebook' ? 'primary' : 'warning'">
              <i :class="data.item.platform === 'facebook' ? 'bi bi-facebook' : 'bi bi-instagram'"></i>
              {{ data.item.platform === 'facebook' ? 'Facebook' : 'Instagram' }}
            </b-badge>
          </template>
          <!-- Role Badge -->
          <template #cell(role)="data">
            <b-badge :variant="data.item.role === 'admin' ? 'success' : 'secondary'">
              {{ data.item.role || '-' }}
            </b-badge>
          </template>
          <!-- Aksi -->
          <template #cell(actions)="data">
            <div class="d-flex justify-content-center gap-5px">
              <!-- Edit -->
              <b-button size="sm" variant="outline-info" @click="editUser(data.item)" title="Edit">
                <feather-icon icon="EditIcon" />
              </b-button>
              <!-- Reset Password -->
              <b-button size="sm" variant="outline-warning" @click="openResetPassword(data.item)" title="Reset Password">
                <feather-icon icon="KeyIcon" />
              </b-button>
              <!-- Hapus -->
              <b-button size="sm" variant="outline-danger" @click="deleteUser(data.item)" title="Hapus">
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </b-table>
        <paginationInfo
          :page="pagination.page"
          :items="pagination.items"
          :count="pagination.count"
          @update-value="paginationChanged"
        />
      </div>
    </b-card>

    <!-- Modals -->
    <addSmdUser />
    <editSmdUser :data="selected_user" />
    <resetPasswordSmdUser :data="selected_user" />
  </div>
</template>

<script>
import useJwt from "@/auth/jwt/useJwt";
import loader from "@/views/components/loader.vue";
import addSmdUser from "@/views/components/smd-users/addSmdUser.vue";
import editSmdUser from "@/views/components/smd-users/editSmdUser.vue";
import resetPasswordSmdUser from "@/views/components/smd-users/resetPasswordSmdUser.vue";
import paginationInfo from "@/views/components/paginationInfo.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {
  BCard, BCardHeader, BCardTitle, BCardBody,
  BInputGroup, BInputGroupPrepend, BFormInput,
  BButton, BTable, BBadge, VBModal,
} from "bootstrap-vue";

export default {
  name: "SmdUser",
  components: {
    BCard, BCardHeader, BCardTitle, BCardBody,
    BInputGroup, BInputGroupPrepend, BFormInput,
    BButton, BTable, BBadge,
    paginationInfo, loader,
    addSmdUser, editSmdUser, resetPasswordSmdUser,
  },
  data() {
    return {
      is_loading: true,
      filter_data: { key: "", platform: "all" },
      pagination: { page: 1, items: 10, count: 0 },
      table_items: [],
      selected_user: null,
      table_fields: [
        { key: "no", label: "No", thClass: "text-center", tdClass: "text-center", thStyle: { width: "1rem" } },
        { key: "username", label: "Username", sortable: true, thClass: "text-left", tdClass: "text-left" },
        { key: "role", label: "Role", thClass: "text-center", tdClass: "text-center" },
        { key: "platform", label: "Platform", thClass: "text-center", tdClass: "text-center" },
      ],
    };
  },
  computed: {
    user_role() {
      const user = this.$store.getters["data/user"] || {};
      return user.role;
    }
  },
  created() {
    this.getData();
    if (this.user_role == 0) {
      this.table_fields.push({
        key: "actions", label: "Aksi", thClass: "text-center", tdClass: "text-center", thStyle: { width: "8rem" }
      });
    }
  },
  watch: {
    "filter_data.key"() {
      if (!this.filter_data.key || this.filter_data.key.trim().length === 0 || this.filter_data.key.trim().length >= 2) {
        this.getData();
      }
    },
  },
  methods: {
    paginationChanged(data) {
      this.pagination = data;
      this.getData();
    },
    getData() {
      this.is_loading = true;
      const params = {
        platform: this.filter_data.platform,
        username: this.filter_data.key || undefined,
        page: this.pagination.page,
        limit: this.pagination.items,
      };
      const query = Object.keys(params)
        .filter((k) => params[k] !== undefined)
        .map((k) => k + "=" + params[k])
        .join("&");
      const api = process.env.VUE_APP_API_URL + "smd-users/?" + query;
      useJwt.axiosIns
        .get(api)
        .then((res) => {
          this.table_items = res.data.users || [];
          this.pagination.count = res.data.page_info[0]?.total || 0;
        })
        .finally(() => { this.is_loading = false; });
    },
    editUser(user) {
      this.selected_user = { ...user };
      this.$bvModal.show("modal-edit-smd-user");
    },
    openResetPassword(user) {
      this.selected_user = { ...user };
      this.$bvModal.show("modal-reset-password-smd-user");
    },
    deleteUser(user) {
      this.$swal({
        title: "Apakah Anda Yakin?",
        html: `User <strong>${user.username}</strong> (${user.platform === 'facebook' ? 'Facebook' : 'Instagram'}) akan dihapus`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, Hapus!",
        cancelButtonText: "Batal",
        customClass: { confirmButton: "btn btn-danger", cancelButton: "btn btn-outline-secondary ml-1" },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          const api = `${process.env.VUE_APP_API_URL}smd-users/delete/${user._id}?platform=${user.platform}`;
          useJwt.axiosIns
            .delete(api)
            .then(() => {
              this.getData();
              this.$toast({ component: ToastificationContent, position: "top-right",
                props: { title: "Berhasil", icon: "CheckCircleIcon", variant: "success", text: "User sosmed berhasil dihapus" },
              });
            })
            .catch(() => {
              this.$toast({ component: ToastificationContent, position: "top-right",
                props: { title: "Gagal!", icon: "AlertCircleIcon", variant: "danger", text: "Gagal menghapus user sosmed" },
              });
            });
        }
      });
    },
  },
  directives: { "b-modal": VBModal },
};
</script>
